import { LitElement, html, nothing } from 'lit';
import { loadOneButton } from '../one-button';
import { customElement, state } from 'lit/decorators.js';
import { api } from '../api';
import { currentTarget, errorText, pairFor, syncLanguage, t, targetDir, targetLangAttr, tOr } from '../i18n';
import { backGlyph } from '../bidi-html';
import { go } from '../router';
import { onSpeaking, speakGeneral, stopSpeaking } from '../tts';
import type { PlacementPublicTask, PlacementScored, PlacementSummary } from '../types';
import { renderWaysIn } from './ways-in';
import { beginFirstRunJourney, firstRunMilestone } from '../research-client';

/** Ids are stored in the learner profile; the labels are translated at render time. */
const CAN_DO = ['order_food', 'follow_conversation', 'introduce', 'ask_prices'] as const;

/** Stored verbatim in the profile (the prompts read them), shown translated. */
const PRIORS = ['just starting', 'a few phrases', 'an evening class', 'living here a while'] as const;

/**
 * `lang` for the placement answer box. Every bank's typed answers are Latin
 * (a Persian learner types Finglish), so the box is left-to-right whatever the
 * target's direction, tagged `-Latn` for a right-to-left target.
 */
function placementInputLang(): string | undefined {
  const lang = targetLangAttr();
  return lang && targetDir() === 'rtl' ? `${lang}-Latn` : lang;
}

/**
 * Onboarding: 1 minute of goals + can-do self-report, then an optional ~3
 * minute adaptive check (reviewed bank, harder after correct, easier after a
 * miss). Everything is skippable — the result is a starting estimate that
 * personalises lesson selection, never a certified level.
 */
@customElement('onboarding-view')
export class OnboardingView extends LitElement {
  /** `first`: the last screen for a learner with no moment yet who set up without the check. */
  @state() private phase: 'about' | 'check' | 'done' | 'first' = 'about';
  @state() private goals = '';
  @state() private prior = '';
  @state() private canDo: Record<string, boolean> = {};
  @state() private saving = false;
  @state() private msg = '';

  @state() private answers: { id: string; value?: unknown }[] = [];
  @state() private task: PlacementPublicTask | null = null;
  @state() private scored: PlacementScored[] = [];
  @state() private progress = { answered: 0, total: 8 };
  @state() private summary: PlacementSummary | null = null;
  @state() private loading = false;
  @state() private error = '';
  @state() private input = '';
  @state() private playingText: string | null = null;
  /**
   * Targets without a placement bank (English, Spanish) never offer the check;
   * null until the pair resolves, so the check button never flashes up for them.
   */
  @state() private placement: boolean | null = null;
  /** Personal mode (no beta): the field signals' privacy line shows here, as the join page shows it in the beta (ticket 43). */
  @state() private personal = false;
  private unsubSpeaking?: () => void;
  private setupForFirstRun = false;
  private firstRunSetupReady: Promise<void> = Promise.resolve();

  protected createRenderRoot() { return this; }

  connectedCallback() {
    super.connectedCallback();
    void loadOneButton().then((on) => on && this.requestUpdate());
    this.unsubSpeaking = onSpeaking((text) => (this.playingText = text));
    void this.resolvePair();
    api.health().then((h) => (this.personal = !h.beta)).catch(() => {});
    this.firstRunSetupReady = this.startFirstRunEvidence();
  }

  private async startFirstRunEvidence() {
    this.setupForFirstRun = await this.firstRun();
    if (!this.setupForFirstRun) return;
    // Research startup can stall on a network request; it must never hold the
    // learner's setup flow open. A delayed start may leave this milestone out
    // of the evidence, which the owner timeline already reports as incomplete.
    void beginFirstRunJourney()
      .then(() => firstRunMilestone('onboarding.entered'))
      .catch(() => {});
  }

  /** A slow moment-list read must not keep goal saving or a completed check waiting. */
  private async waitForFirstRunSetup() {
    let timer: ReturnType<typeof setTimeout> | undefined;
    await Promise.race([
      this.firstRunSetupReady,
      new Promise<void>((resolve) => { timer = setTimeout(resolve, 1000); }),
    ]);
    if (timer) clearTimeout(timer);
  }

  /** The learner's pair decides whether a starting check exists at all. */
  private async resolvePair() {
    try {
      await syncLanguage();
    } catch { /* offline — assume the check exists, the route answers for real */ }
    this.placement = pairFor(currentTarget())?.placement ?? true;
    // The home screen's "Take the 3-minute starting check" (a learner who set
    // up without it): straight to the check, the saved goals untouched.
    if (location.hash === '#check' && this.placement && this.phase === 'about') {
      this.phase = 'check';
      await this.round([]);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.unsubSpeaking?.();
    stopSpeaking();
  }

  private toggleCanDo(id: string) {
    this.canDo = { ...this.canDo, [id]: !this.canDo[id] };
  }

  private save = async (startCheck: boolean) => {
    this.saving = true;
    this.msg = '';
    try {
      await api.saveLearner({ goals: this.goals.trim() || undefined, prior: this.prior || undefined, canDo: this.canDo });
      // The first-run read may still be in flight: a delayed "no moment yet"
      // answer still counts, so wait for it before choosing the destination.
      await this.waitForFirstRunSetup();
      if (startCheck) {
        this.phase = 'check';
        await this.round([]);
      } else if (this.setupForFirstRun) {
        firstRunMilestone('onboarding.completed');
        this.phase = 'first';
      } else {
        go('/', true);
      }
    } catch (e) {
      this.msg = errorText(e);
    } finally {
      this.saving = false;
    }
  };

  /**
   * No moment yet: the setup ends on the two ways into a first moment, not on
   * home. A learner who already has one (editing their goals) goes home.
   */
  private async firstRun(): Promise<boolean> {
    try {
      return (await api.listMoments({ limit: 1 })).total === 0;
    } catch {
      return false;
    }
  }

  /** One adaptive round: send all answers so far, get scores + the next question. */
  private async round(answers: { id: string; value?: unknown }[], finish = false) {
    this.loading = true;
    this.error = '';
    try {
      const r = await api.placement(answers, finish);
      this.answers = answers;
      this.scored = r.scored;
      this.input = '';
      if (r.done) {
        this.summary = r.summary;
        this.task = null;
        this.phase = 'done';
        // As in save(): a delayed first-run read still counts, so the
        // milestone is emitted only after it settles.
        await this.waitForFirstRunSetup();
        if (this.setupForFirstRun) {
          firstRunMilestone('onboarding.completed');
        }
      } else {
        this.task = r.next;
        this.progress = r.progress;
      }
    } catch (e) {
      this.error = errorText(e);
    } finally {
      this.loading = false;
    }
  }

  private answer = (value: unknown) => {
    if (!this.task || this.loading) return;
    void this.round([...this.answers, { id: this.task.id, value }]);
  };

  private skip = () => {
    void this.round(this.answers, true);
  };

  /** Placement listen question: general TTS, same singleton/fallback as moment listen buttons. */
  private playAudio = (text: string) => {
    void speakGeneral(text, { playMs: 8000 });
  };

  render() {
    return html`
      <div class="screen">
        <div class="topbar">
          <button class="btn ghost icon" @click=${() => go('/')} aria-label=${t('common.homeAria')}>${backGlyph()}</button>
          <h1 style="font-size:17px">${t('onboarding.title')}</h1>
        </div>
        ${this.phase === 'about' ? this.renderAbout() : nothing}
        ${this.phase === 'check' ? this.renderCheck() : nothing}
        ${this.phase === 'done' ? this.renderDone() : nothing}
        ${this.phase === 'first' ? this.renderFirst() : nothing}
      </div>
    `;
  }

  private renderAbout() {
    return html`
      <div class="card">
        <h2>${t('onboarding.goalsTitle')}</h2>
        <div class="muted small" style="margin-bottom:8px">${t('onboarding.goalsHint')}</div>
        <textarea class="note" style="min-height:64px" placeholder=${t('onboarding.goalsPlaceholder')}
          .value=${this.goals} @input=${(e: Event) => (this.goals = (e.target as HTMLTextAreaElement).value)}></textarea>
        <div class="muted small" style="margin:10px 0 6px">${t('onboarding.priorTitle')}</div>
        <div style="display:flex;gap:6px;flex-wrap:wrap">
          ${PRIORS.map((p) => html`<button class="btn ${this.prior === p ? 'primary' : ''} small"
            style="min-height:30px;text-transform:none" @click=${() => (this.prior = this.prior === p ? '' : p)}>${t(`prior.${p}`)}</button>`)}
        </div>
        <div class="muted small" style="margin:10px 0 6px">${t('onboarding.canDoTitle')}</div>
        <div style="display:flex;gap:6px;flex-wrap:wrap">
          ${CAN_DO.map((id) => html`<button class="btn ${this.canDo[id] ? 'primary' : ''} small"
            style="min-height:30px;text-transform:none" @click=${() => this.toggleCanDo(id)}>
            ${this.canDo[id] ? '✓ ' : ''}${t(`cando.${id}`)}</button>`)}
        </div>
        ${this.msg ? html`<div class="muted small" style="margin-top:8px">${this.msg}</div>` : nothing}
        <div class="save-row" style="display:flex;gap:8px;margin-top:14px">
          <button class="btn ${this.placement === false ? 'primary' : ''}" style="flex:1" ?disabled=${this.saving} @click=${() => this.save(false)}>${t('onboarding.saveStart')}</button>
          ${this.placement
            ? html`<button class="btn primary" style="flex:1" ?disabled=${this.saving} @click=${() => this.save(true)}>${t('onboarding.saveCheck')}</button>`
            : nothing}
        </div>
        ${this.personal ? html`<p class="muted small privacy-line" style="margin:12px 0 0">${t('privacy.line')}</p>` : nothing}
      </div>
    `;
  }

  private renderCheck() {
    const last = this.scored[this.scored.length - 1];
    return html`
      <div class="card">
        <div class="muted small" style="margin-bottom:8px">${t('onboarding.checkProgress', { done: this.progress.answered, total: this.progress.total })}</div>
        ${this.error ? html`<div class="muted small">${this.error}</div>` : nothing}
        ${this.loading ? html`<div class="empty"><span class="spinner"></span></div>` : nothing}
        ${!this.loading && this.task ? this.renderTask(this.task, last) : nothing}
        <button class="btn ghost small" style="margin-top:12px;text-transform:none" @click=${this.skip}>${t('onboarding.skipRest')}</button>
      </div>
    `;
  }

  private renderTask(task: PlacementPublicTask, last: PlacementScored | undefined) {
    const p = task.props as Record<string, unknown>;
    return html`
      ${last ? html`<div class="note" style="margin-bottom:8px">
        ${last.correct ? t('onboarding.nice') : t('onboarding.notQuite')} ${last.explain ?? ''}
      </div>` : nothing}
      ${task.type === 'listen' ? html`<button class="btn primary block" style="margin-bottom:10px" @click=${() => this.playAudio(String(p.audioText ?? ''))}>
        ${this.playingText ? t('onboarding.playing') : t('onboarding.play')}</button>` : nothing}
      <div><strong dir="auto">${String(p.question ?? p.sentence ?? '')}</strong></div>
      ${p.translation ? html`<div class="muted small" style="margin-top:4px">${String(p.translation)}</div>` : nothing}
      ${(task.type === 'choice' || task.type === 'reply' || task.type === 'listen')
        ? html`<div style="display:flex;flex-direction:column;gap:8px;margin-top:12px">
            ${((p.options ?? []) as unknown[]).map((o, k) => html`<button class="btn block" style="text-transform:none" dir="auto" @click=${() => this.answer(k)}>${String(o)}</button>`)}
          </div>`
        : html`<div style="display:flex;gap:8px;margin-top:12px">
            <input class="note" style="flex:1" placeholder=${t('onboarding.typePlaceholder')} dir="ltr" lang=${placementInputLang() ?? nothing} .value=${this.input}
              @input=${(e: Event) => (this.input = (e.target as HTMLInputElement).value)}
              @keydown=${(e: KeyboardEvent) => { if (e.key === 'Enter' && this.input.trim()) this.answer(this.input.trim()); }} />
            <button class="btn primary" ?disabled=${!this.input.trim()} @click=${() => this.answer(this.input.trim())}>${t('onboarding.check')}</button>
          </div>`}
    `;
  }

  private renderDone() {
    const s = this.summary;
    const bandLabel = (b: string) => tOr(`band.${b}`, b);
    return html`
      <div class="card" style="text-align:center">
        <div style="font-size:32px">✓</div>
        <div><strong>${t('onboarding.doneTitle')}</strong> <span class="muted small">${t('onboarding.doneSub', { n: s?.answered ?? 0 })}</span></div>
        ${s ? html`<div class="small" style="margin-top:8px;text-align:start">
          ${s.perSkill.map((r) => html`<div>• ${tOr(`skill.${r.skill}`, r.skill)}: ${r.correct}/${r.total} — ${bandLabel(s.bands[r.skill])}</div>`)}
          <div class="muted small" style="margin-top:6px">${t('onboarding.speakingNote')}</div>
        </div>` : nothing}
        ${this.renderWaysIn()}
      </div>
    `;
  }

  /** Set up without the check, no moment yet: the two ways in, and nothing else to do. */
  private renderFirst() {
    return html`
      <div class="card first-moment">
        <h2>${t('onboarding.firstTitle')}</h2>
        <div class="muted small">${t('onboarding.firstSub')}</div>
        ${this.renderWaysIn()}
      </div>
    `;
  }

  /** Record and Type, the two equal ways in (ticket 36, `ways-in.ts`). */
  private renderWaysIn() {
    return renderWaysIn(t('onboarding.recordFirst'), t('onboarding.typeFirst'));
  }
}
